import { createMFHPortfolio, fetchMfhPortfolioList, fetchOneMfhPortfolio, fetchPipelineMfhPortfolios, updateMFHPortfolio } from '@/api/mfh';
import { useRouteParams } from '@/hooks';
import { withLoading } from '@/shared';
import type {
    IMFHPortfolio,
    IMfhFilter,
    IMfhPortfolioQuery,
    IPaginationFields,
    IPipelineMFH,
    IPipelineMFHPortfolio,
    MFHPortfolioExpandOpts,
} from '@condo/domain';
import { MFHPortfolioPurchasingState } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, ref, unref } from 'vue';

export const useMfhPortfolios = (
    queryOpts?: MaybeRef<IMfhPortfolioQuery>,
    expandOptions?: MFHPortfolioExpandOpts,
    pagination?: MaybeRef<IPaginationFields>,
    enabled: MaybeRef<boolean> = true,
) => {
    const query = useQuery(
        ['mfh-portfolios', queryOpts, pagination],
        async (): Promise<{ mfhPortfolios: IMFHPortfolio[]; total: number }> => {
            return fetchMfhPortfolioList(unref(queryOpts), expandOptions, unref(pagination));
        },
        {
            initialData: { mfhPortfolios: [], total: 0 },
            enabled,
        },
    );

    const { mutateAsync: createMfhPortfolioMutation } = useMutation(
        async (payload: { name: string; mfhIds: number[]; assetPartnerId: number }) => withLoading(async () => createMFHPortfolio(payload)),
        {
            onSuccess: () => {
                if (unref(enabled)) {
                    query.refetch();
                }
            },
        },
    );

    const { mutateAsync: updateMFHPortfolioMutation } = useMutation(
        async ({ mfhPortfolioId, payload }: { mfhPortfolioId: number; payload: { name?: string; mfhIds?: number[] } }) =>
            withLoading(async () => updateMFHPortfolio(mfhPortfolioId, payload)),
        {
            onSuccess: () => {
                if (unref(enabled)) {
                    query.refetch();
                }
            },
        },
    );

    return { ...query, createMfhPortfolio: createMfhPortfolioMutation, updateMFHPortfolio: updateMFHPortfolioMutation };
};

const DEFAULT_EXPAND_OPTS: MFHPortfolioExpandOpts = {
    select: ['mfhPortfolioId', 'name', 'condoUuid', 'condoUuid', 'assetPartnerId'],
    expand: {
        mfhs: {
            select: ['mfhId'],
            expand: {
                attributes: {
                    select: [
                        'mfhAttrId',
                        'rentalIncomeActual',
                        'energyConsumptionCategory',
                        'restrictions',
                        'energyCertificateAvailability',
                        'energyCertificateCreationDate',
                        'buildingEnergyRating',
                        'energyConsumption',
                        'primaryEnergyDemand',
                        'endEnergyDemand',
                        'numberOfApartments',
                        'numberOfCommercialUnits',
                        'estimatedLivingSpace',
                        'commercialSpace',
                    ],
                },
                buildings: {
                    select: ['mfhBuildingId'],
                    expand: {
                        estates: {
                            select: ['estateId'],
                            expand: {
                                attributes: {
                                    select: ['livingSpace'],
                                },
                            },
                        },
                    },
                },
                price: {
                    select: ['priceId', 'purchasePriceNett', 'pricePerSQM'],
                },
                sourcing: {
                    select: ['processId', 'purchasingEntityId'],
                    expand: {
                        purchasingEntity: {
                            select: ['companyId', 'shortName'],
                        },
                    },
                },
            },
        },
        financing: {
            select: ['processId', 'mfhPortfolioId', 'processType', 'processState', 'attributes', 'finishedAt'],
        },
        purchasing: {
            select: ['processId', 'mfhPortfolioId', 'processType', 'processState', 'attributes', 'finishedAt'],
        },
        assetPartner: {
            select: ['companyId', 'name'],
        },
    },
};

export const useMfhPortfolio = (mfhPortfolioId: MaybeRef<number | undefined>, expandOpts: MaybeRef<MFHPortfolioExpandOpts> = DEFAULT_EXPAND_OPTS) => {
    const queryKey = computed(() => ['mfh-portfolio', mfhPortfolioId, expandOpts]);
    const enabled = computed(() => !!unref(mfhPortfolioId));

    return useQuery(queryKey, async () => fetchOneMfhPortfolio(unref(mfhPortfolioId)!, unref(expandOpts)), {
        initialData: null,
        enabled,
    });
};

export const useMfhPortfolioMutations = (mfhPortfolioId: MaybeRef<number>) => {
    const queryClient = useQueryClient();
    const queryPrefix = computed(() => ['mfh-portfolio', unref(mfhPortfolioId)]);

    const { mutateAsync: updateMFHPortfolioMutation } = useMutation(
        async (payload: { name?: string; mfhIds?: number[] }) => withLoading(async () => updateMFHPortfolio(unref(mfhPortfolioId), payload)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryPrefix.value);
            },
        },
    );

    return { updateMFHPortfolio: updateMFHPortfolioMutation };
};

export const usePipelineMfhPortfolios = (queryOpts?: MaybeRef<IMfhFilter>, enabled = true) => {
    return useQuery(
        ['pipeline-mfh-portfolios', queryOpts],
        async (): Promise<{ portfolios: IPipelineMFHPortfolio[]; mfhsWithoutPortfolio: IPipelineMFH[]; total: number }> =>
            withLoading(async () => fetchPipelineMfhPortfolios(unref(queryOpts))),
        {
            initialData: { portfolios: [], mfhsWithoutPortfolio: [], total: 0 },
            enabled,
        },
    );
};

export const useMfhPortfolioFilter = (initialOverride?: Record<any, any>) => {
    const { getRouteParam, getRouteParamArray } = useRouteParams();

    const defaultFilter = {
        assetPartnerId: [],
        totalPriceFrom: undefined,
        totalPriceTo: undefined,
        totalRentalIncomePerYearFrom: undefined,
        totalRentalIncomePerYearTo: undefined,
        purchasingState: Object.values(MFHPortfolioPurchasingState).filter(state => state !== MFHPortfolioPurchasingState.Rejected),
    } as IMfhPortfolioQuery;

    const defaultPagination = { page: 1, size: 50 } as IPaginationFields;

    const purchasingState = getRouteParamArray('purchasingState', String) as MFHPortfolioPurchasingState[];
    const filter = ref<IMfhPortfolioQuery>({
        assetPartnerId: getRouteParamArray('assetPartnerId', Number) ?? defaultFilter.assetPartnerId,
        totalPriceFrom: getRouteParam('totalPriceFrom', Number) ?? defaultFilter.totalPriceFrom,
        totalPriceTo: getRouteParam('totalPriceTo', Number) ?? defaultFilter.totalPriceTo,
        totalRentalIncomePerYearFrom: getRouteParam('totalRentalIncomePerYearFrom', Number) ?? defaultFilter.totalRentalIncomePerYearFrom,
        totalRentalIncomePerYearTo: getRouteParam('totalRentalIncomePerYearTo', Number) ?? defaultFilter.totalRentalIncomePerYearTo,
        purchasingState: purchasingState.length ? purchasingState : defaultFilter.purchasingState,
        ...(initialOverride ?? {}),
    });

    const pagination = ref<IPaginationFields>({
        page: getRouteParam('page', Number) ?? defaultPagination.page,
        size: getRouteParam('size', Number) ?? defaultPagination.size,
    });

    const setFilter = async (updatedFilter?: Partial<IMfhPortfolioQuery>) => {
        filter.value = { ...defaultFilter, ...initialOverride, ...updatedFilter };
    };

    const setPagination = async (updatedPagination?: Partial<IPaginationFields>) => {
        pagination.value = { ...defaultPagination, ...updatedPagination };
    };

    const resetFilter = async () => {
        filter.value = { ...defaultFilter, ...initialOverride };
    };

    const resetPagination = async () => {
        pagination.value = { ...defaultPagination };
    };

    return { filter, setFilter, resetFilter, pagination, setPagination, resetPagination };
};
